import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { formatTime } from "@/lib/format";
import type { Doc } from "../../convex/_generated/dataModel";
import { entryKcal, formatQuantityUnit } from "../screens/today-utils";
import { SourceChip } from "./SourceChip";
import { Apple } from "lucide-react";

type FoodEntry = Doc<"foodEntries">;

export interface MealCardProps {
  entry: FoodEntry;
  onEdit: (entry: FoodEntry) => void;
}

const CONFIDENCE_DOT_CLASS: Record<string, string> = {
  high: "bg-accent",
  medium: "bg-warning",
  low: "bg-danger",
};

export function MealCard({ entry, onEdit }: MealCardProps) {
  // Unknown calories render as "—" (never 0); a measured 0 stays 0.
  const kcal = entryKcal(entry);
  const time = entry.loggedAt ?? entry._creationTime;
  const quantityUnit = formatQuantityUnit(entry.quantity, entry.unit);
  const confidenceDot =
    entry.confidence !== undefined
      ? (CONFIDENCE_DOT_CLASS[entry.confidence] ?? "bg-warning")
      : null;

  return (
    <Card
      data-testid={`meal-card-${entry._id}`}
      className={cn(
        "flex items-center justify-between gap-3 p-4",
        "transition-transform duration-200 ease-out",
        "active:scale-[0.98] select-none cursor-pointer",
      )}
      onClick={() => onEdit(entry)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(entry);
        }
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="rounded-full bg-accent-light p-2 text-accent shrink-0">
          <Apple size={20} strokeWidth={2} />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium text-text truncate">
            {entry.name}
          </span>
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-text-muted">
            <span>{formatTime(time)}</span>
            {quantityUnit !== null && (
              <span data-testid="meal-quantity">· {quantityUnit}</span>
            )}
            <SourceChip dataSource={entry.dataSource} testId="meal-source" />
            {confidenceDot !== null && (
              <span
                data-testid="meal-confidence"
                role="img"
                aria-label={`Confidence: ${entry.confidence}`}
                title={`Confidence: ${entry.confidence}`}
                className={cn("inline-block h-2 w-2 rounded-full", confidenceDot)}
              />
            )}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span data-testid="meal-kcal" className="text-sm font-semibold text-text">
          {kcal === undefined ? "—" : `${kcal} kcal`}
        </span>
        <div data-testid="meal-macros" className="flex max-w-[150px] flex-wrap justify-end gap-x-2 gap-y-0.5 text-[11px] text-text-muted">
          <span data-testid="meal-protein">P {entry.proteinG ?? "—"}g</span>
          <span data-testid="meal-carbs">C {entry.carbsG ?? "—"}g</span>
          <span data-testid="meal-fat">F {entry.fatG ?? "—"}g</span>
          <span data-testid="meal-fiber">Fi {entry.fiberG ?? "—"}g</span>
        </div>
      </div>
    </Card>
  );
}
